import { find, get, includes, map, orderBy } from "lodash";
import { Fragment, useEffect, useMemo } from "react";
import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  FormHelperText,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { CloseOutlined, UploadFileOutlined } from "@mui/icons-material";
import { FieldArray } from "formik";
import FormikCheckBox from "components/base/FormikCheckBox";
import FormikSelectField from "components/base/FormikSelectField";
import FormikTextField from "components/base/FormikTextField";
import { GRADE_LEVELS, K12GradeLevels } from "../models";
import FormikFileInput from "components/base/FormikFileInput";
import { useGeoData } from "features/GeoData";
import axios from "axios";

const PRIORITY_COUNTRIES = ["USA"];

export default function FormFields({
  values,
  errors,
  handleChange,
  handleBlur,
  touched,
  setFieldValue,
  setFieldTouched,
}) {
  const {
    firstName,
    lastName,
    email,
    localSchoolDistrict,
    isDataCollectionAgreementAccepted,
    streetAddress,
    city,
    state,
    zipCode,
    country,
    students,
  } = values;
  const { countries, activeRegions, updateActiveRegions } = useGeoData(axios);

  const regions = useMemo(() => {
    return orderBy(
      map(activeRegions, (region) => ({
        key: region.state_code,
        label: region.name,
      })),
      ["label"]
    );
  }, [activeRegions]);

  function createNewStudent() {
    return {
      firstName: "",
      lastName: "",
      gradeLevel: K12GradeLevels.GK,
      email: "",
      letterOfIntent: null,
    };
  }

  useEffect(() => {
    async function fetchRegions() {
      const countryObj = find(countries, { iso3: country });
      if (countryObj != null) {
        await updateActiveRegions(countryObj.id);
      }
    }

    fetchRegions();
  }, [countries, country]);

  return (
    <Fragment>
      <Stack direction="row">
        <FormikTextField
          fullWidth
          error={get(errors, "firstName")}
          touched={get(touched, "firstName", null)}
          handleBlur={handleBlur}
          handleChange={handleChange}
          value={firstName}
          label="First Name"
          name="firstName"
        />
        <FormikTextField
          fullWidth
          error={get(errors, "lastName")}
          touched={get(touched, "lastName", null)}
          handleBlur={handleBlur}
          handleChange={handleChange}
          value={lastName}
          label="Last Name"
          name="lastName"
        />
      </Stack>
      <FormikTextField
        fullWidth
        error={get(errors, "email")}
        touched={get(touched, "email", null)}
        handleBlur={handleBlur}
        handleChange={handleChange}
        value={email}
        label="Parent/Guardian Email"
        name="email"
      />
      <FormikTextField
        fullWidth
        error={get(errors, "localSchoolDistrict")}
        touched={get(touched, "localSchoolDistrict", null)}
        handleBlur={handleBlur}
        handleChange={handleChange}
        value={localSchoolDistrict}
        label="Local School District"
        name="localSchoolDistrict"
      />
      <FormikSelectField
        error={get(errors, "country")}
        handleBlur={handleBlur}
        handleChange={handleChange}
        touched={get(touched, "country", null)}
        value={country}
        label="Country"
        name="country"
        id="country-select"
        size="small"
        options={map(countries, (country) => ({
          key: country.iso3,
          label: `${country.emoji} ${country.name} ${
            country.native != null &&
            country.name.toLowerCase() !== country.native.toLowerCase()
              ? `(${country.native})`
              : ""
          }`,
          default: country.iso3 === "USA",
        })).sort((countryA, countryB) => {
          const aPriority = includes(PRIORITY_COUNTRIES, countryA.key);
          const bPriority = includes(PRIORITY_COUNTRIES, countryB.key);
          if (aPriority && bPriority) {
            return countryA.name - countryB.name;
          }
          if (aPriority) {
            return -1;
          }
          if (bPriority) {
            return 1;
          }
          return countryA.name - countryB.name;
        })}
      />
      <FormikTextField
        error={get(errors, "streetAddress")}
        touched={get(touched, "streetAddress", null)}
        handleBlur={handleBlur}
        handleChange={handleChange}
        value={streetAddress}
        label="Street Address"
        name="streetAddress"
      />
      <Stack direction="row">
        <FormikTextField
          error={get(errors, "city")}
          touched={get(touched, "city", null)}
          handleBlur={handleBlur}
          handleChange={handleChange}
          value={city}
          label="City"
          name="city"
          sx={{
            flex: 5,
          }}
        />
        <FormikSelectField
          error={get(errors, "state")}
          handleBlur={handleBlur}
          handleChange={handleChange}
          touched={get(touched, "state", null)}
          value={state}
          label="State/Region"
          name="state"
          id="state-select"
          options={regions}
          size="small"
          sx={{
            flex: 4,
          }}
          disabled={regions.length < 1}
          helperText={
            regions.length > 1 ? null : "Country has no states/regions"
          }
        />
        <FormikTextField
          error={get(errors, "zipCode")}
          touched={get(touched, "zipCode", null)}
          handleBlur={handleBlur}
          handleChange={handleChange}
          value={zipCode}
          label="Zip Code"
          name="zipCode"
          sx={{
            flex: 3,
          }}
        />
      </Stack>
      <FieldArray name="students">
        {({ push, remove }) => {
          return (
            <Fragment>
              {students.map((student, index) => (
                <Card
                  key={`student-${index}`}
                  elevation={0}
                  sx={({ palette }) => {
                    return {
                      borderBottom: `1px solid ${palette.divider}`,
                      overflow: "unset",
                    };
                  }}
                >
                  <CardHeader title={`Student #${index + 1}`} />
                  <CardContent>
                    <Stack direction="column">
                      <Stack direction="row">
                        <FormikTextField
                          fullWidth
                          error={get(errors, `students[${index}].firstName`)}
                          touched={get(
                            touched,
                            `students[${index}].firstName`,
                            null
                          )}
                          handleBlur={handleBlur}
                          handleChange={handleChange}
                          value={student.firstName}
                          label="First Name"
                          name={`students[${index}].firstName`}
                        />
                        <FormikTextField
                          fullWidth
                          error={get(errors, `students[${index}].lastName`)}
                          touched={get(
                            touched,
                            `students[${index}].lastName`,
                            null
                          )}
                          handleBlur={handleBlur}
                          handleChange={handleChange}
                          value={student.lastName}
                          label="Last Name"
                          name={`students[${index}].lastName`}
                        />
                      </Stack>
                      <FormikSelectField
                        error={get(errors, `students[${index}].gradeLevel`)}
                        handleBlur={handleBlur}
                        handleChange={handleChange}
                        touched={get(
                          touched,
                          `students[${index}].gradeLevel`,
                          null
                        )}
                        value={student.gradeLevel}
                        label="Grade Level"
                        name={`students[${index}].gradeLevel`}
                        id="grade-level-select"
                        options={GRADE_LEVELS}
                        size="small"
                        sx={{
                          flex: 4,
                        }}
                      />
                      <FormikTextField
                        fullWidth
                        error={get(errors, `students[${index}].email`)}
                        touched={get(touched, `students[${index}].email`, null)}
                        handleBlur={handleBlur}
                        handleChange={handleChange}
                        value={student.email}
                        label="Student Email (Optional)"
                        name={`students[${index}].email`}
                        helperText="Leave blank if the student will use the Parent / Guardian email to log into Sooth. If you add a student email here, an account will be created using this email."
                      />
                      <FormikFileInput
                        handleChange={(acceptedFiles) => {
                          setFieldValue(
                            `students[${index}].letterOfIntent`,
                            acceptedFiles,
                            true
                          );
                          setFieldTouched(
                            `students[${index}].letterOfIntent`,
                            true,
                            false
                          );
                        }}
                        error={get(errors, `students[${index}].letterOfIntent`)}
                        touched={get(
                          touched,
                          `students[${index}].letterOfIntent`,
                          null
                        )}
                        helperText={
                          <Stack direction="row" alignItems="center" gap={2}>
                            <UploadFileOutlined />
                            <Typography variant="body2" align="center">
                              Click to upload or drag your Notice of Intent to
                              Homeschool (or equivalent) here.
                            </Typography>
                          </Stack>
                        }
                        alertProps={{
                          variant: "outlined",
                          sx: {
                            BorderStyle: "dotted",
                          },
                        }}
                        accept={{
                          "application/pdf": [".pdf"],
                          // "application/msword": [".doc", ".docx"],
                          // "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
                          //   [".doc", ".docx"],
                        }}
                        maxFiles={1}
                        multiple={false}
                        value={student.letterOfIntent}
                      />
                      <FormHelperText fontSize="0.8rem" sx={{ px: 2 }}>
                        Note: Please ensure the information provided in the
                        Notice of Intent (or equivalent) is the same as the
                        information you provided in this form. We require this
                        document to verify that the account associated with a
                        generic email belongs to a homeschooled student.
                      </FormHelperText>
                    </Stack>
                  </CardContent>
                  <CardActions
                    sx={{
                      justifyContent: "end",
                    }}
                  >
                    <IconButton
                      size="small"
                      onClick={() => remove(index)}
                      disabled={students.length < 2}
                    >
                      <CloseOutlined fontSize="inherit" />
                    </IconButton>
                  </CardActions>
                </Card>
              ))}
              <Stack direction="row" justifyContent="end">
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => push(createNewStudent())}
                >
                  Add Student
                </Button>
              </Stack>
            </Fragment>
          );
        }}
      </FieldArray>
      <FormikCheckBox
        handleBlur={handleBlur}
        handleChange={handleChange}
        value={isDataCollectionAgreementAccepted}
        name="isDataCollectionAgreementAccepted"
        error={get(errors, "isDataCollectionAgreementAccepted")}
        touched={get(touched, "isDataCollectionAgreementAccepted", null)}
        label="I understand Sooth Inc. requires this personal information for student status verification and account creation purposes only. Sooth Inc. will never use any of my personal information for any other purposes whatsoever. The information I have provided will remain encrypted and will be treated in accordance with Sooth Inc.'s Privacy Policy. I retain the right to have all my information deleted by Sooth Inc. upon my request."
      />
    </Fragment>
  );
}
